<template>
  <v-text-field
    v-model="displayValue"
    :label="label"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :rules="rules"
    @input="onInput"
    @focus="onFocus"
    @blur="onBlur"
    @keydown="onKeyDown"
    ref="input"
  >
    <template v-slot:append>
      <v-menu
        v-model="menu"
        :close-on-content-click="false"
        :nudge-right="40"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            v-bind="attrs"
            v-on="on"
            @click="openCalendar"
            :disabled="disabled || readonly"
          >
            mdi-calendar
          </v-icon>
        </template>

        <v-date-picker
          v-if="!showTimePicker"
          v-model="dateValue"
          no-title
          scrollable
          locale="pt-BR"
          color="primary"
          @change="onDateSelected"
        >
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="menu = false">Cancelar</v-btn>
          <v-btn
            v-if="includeTime"
            text
            color="primary"
            @click="showTimePicker = true"
            :disabled="!dateValue"
          >
            Definir Hora
          </v-btn>
          <v-btn
            v-else
            text
            color="primary"
            @click="confirmDate"
            :disabled="!dateValue"
          >
            OK
          </v-btn>
        </v-date-picker>

        <v-time-picker
          v-if="showTimePicker && includeTime"
          v-model="timeValue"
          no-title
          format="24hr"
          scrollable
          color="primary"
        >
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="showTimePicker = false"
            >Voltar</v-btn
          >
          <v-btn text color="primary" @click="confirmDateTime">OK</v-btn>
        </v-time-picker>
      </v-menu>
    </template>
  </v-text-field>
</template>

<script>
import moment from "moment";

export default {
  name: "SmartDateInput",
  props: {
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "Data",
    },
    includeTime: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      menu: false,
      showTimePicker: false,
      dateValue: null,
      timeValue: "09:00",
      internalValue: "",
      lastValidValue: "",
      cursorPosition: 0,
      isUpdatingFromProps: false,
    };
  },
  computed: {
    displayValue: {
      get() {
        return this.internalValue;
      },
      set(value) {
        this.internalValue = value;
      },
    },
    expectedFormat() {
      return this.includeTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY";
    },
    maskPattern() {
      return this.includeTime ? "##/##/#### ##:##" : "##/##/####";
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (!this.isUpdatingFromProps) {
          this.updateFromExternalValue(newValue);
        }
      },
    },
  },
  methods: {
    updateFromExternalValue(value) {
      this.isUpdatingFromProps = true;

      if (!value) {
        this.internalValue = "";
        this.dateValue = null;
        this.timeValue = "09:00";
      } else {
        // Se já está no formato correto, usar diretamente
        if (this.isValidDisplayFormat(value)) {
          this.internalValue = value;
          this.parseInternalValue();
        } else {
          // Tentar converter de outros formatos
          const momentDate = moment(value);
          if (momentDate.isValid()) {
            this.internalValue = momentDate.format(this.expectedFormat);
            this.dateValue = momentDate.format("YYYY-MM-DD");
            this.timeValue = momentDate.format("HH:mm");
          } else {
            this.internalValue = value;
          }
        }
      }

      this.lastValidValue = this.internalValue;
      this.$nextTick(() => {
        this.isUpdatingFromProps = false;
      });
    },

    isValidDisplayFormat(value) {
      if (!value) return false;

      if (this.includeTime) {
        return /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(value);
      } else {
        return /^\d{2}\/\d{2}\/\d{4}$/.test(value);
      }
    },

    parseInternalValue() {
      if (this.isValidDisplayFormat(this.internalValue)) {
        const momentDate = moment(this.internalValue, this.expectedFormat);
        if (momentDate.isValid()) {
          this.dateValue = momentDate.format("YYYY-MM-DD");
          this.timeValue = momentDate.format("HH:mm");
          return true;
        }
      }
      return false;
    },

    onInput(value) {
      const input = this.$refs.input.$refs.input;
      const currentCursor = input.selectionStart;

      // Aplicar máscara mantendo posição do cursor
      this.applyMaskWithCursor(value, currentCursor);
    },

    onKeyDown(event) {
      const input = this.$refs.input.$refs.input;

      // Permitir navegação e edição mais natural
      if (event.key === "Backspace" || event.key === "Delete") {
        return; // Permitir comportamento padrão
      }

      // Para dígitos, interceptar e controlar a inserção
      if (/^\d$/.test(event.key)) {
        event.preventDefault();
        this.insertDigitAtCursor(event.key, input.selectionStart);
      }
    },

    insertDigitAtCursor(digit, cursorPos) {
      const currentValue = this.internalValue;

      // Encontrar posição do dígito na string de números
      let digitPosition = 0;
      for (let i = 0; i < cursorPos && i < currentValue.length; i++) {
        if (/\d/.test(currentValue[i])) {
          digitPosition++;
        }
      }

      // Extrair números atuais
      const currentNumbers = currentValue.replace(/[^\d]/g, "");

      // Inserir novo dígito na posição correta
      const newNumbers =
        currentNumbers.slice(0, digitPosition) +
        digit +
        currentNumbers.slice(digitPosition);

      // Aplicar máscara aos novos números
      this.applyMaskToNumbers(newNumbers, digitPosition + 1);
    },

    applyMaskToNumbers(numbers, targetDigitPosition) {
      const maxLength = this.includeTime ? 12 : 8;
      const limitedNumbers = numbers.slice(0, maxLength);

      let masked = "";

      // Aplicar máscara
      for (let i = 0; i < limitedNumbers.length; i++) {
        if (this.includeTime) {
          // DD/MM/YYYY HH:mm
          if (i === 2 || i === 4) masked += "/";
          else if (i === 8) masked += " ";
          else if (i === 10) masked += ":";
        } else {
          // DD/MM/YYYY
          if (i === 2 || i === 4) masked += "/";
        }
        masked += limitedNumbers[i];
      }

      this.internalValue = masked;

      // Calcular nova posição do cursor baseada na posição do dígito
      let newCursorPos = 0;
      let digitCount = 0;

      for (let i = 0; i < masked.length; i++) {
        if (/\d/.test(masked[i])) {
          digitCount++;
          if (digitCount >= targetDigitPosition) {
            newCursorPos = i + 1;
            break;
          }
        }
        if (digitCount < targetDigitPosition) {
          newCursorPos = i + 1;
        }
      }

      // Aplicar nova posição do cursor
      this.$nextTick(() => {
        const input = this.$refs.input.$refs.input;
        if (input) {
          input.setSelectionRange(newCursorPos, newCursorPos);
        }
      });
    },

    applyMaskWithCursor(value, cursorPos) {
      // Esta função é chamada principalmente para backspace/delete
      const numbersOnly = value.replace(/[^\d]/g, "");

      if (!numbersOnly) {
        this.internalValue = "";
        return;
      }

      const maxLength = this.includeTime ? 12 : 8;
      let masked = "";

      // Aplicar máscara
      for (let i = 0; i < numbersOnly.length && i < maxLength; i++) {
        if (this.includeTime) {
          // DD/MM/YYYY HH:mm
          if (i === 2 || i === 4) masked += "/";
          else if (i === 8) masked += " ";
          else if (i === 10) masked += ":";
        } else {
          // DD/MM/YYYY
          if (i === 2 || i === 4) masked += "/";
        }
        masked += numbersOnly[i];
      }

      this.internalValue = masked;

      // Para backspace/delete, tentar manter cursor numa posição sensata
      this.$nextTick(() => {
        const input = this.$refs.input.$refs.input;
        if (input) {
          const newPos = Math.min(cursorPos, masked.length);
          input.setSelectionRange(newPos, newPos);
        }
      });
    },

    onFocus() {
      // Salvar valor válido atual
      if (this.isValidDisplayFormat(this.internalValue)) {
        this.lastValidValue = this.internalValue;
      }
    },

    onBlur() {
      this.validateAndEmit();
    },

    validateAndEmit() {
      const value = this.internalValue;

      if (!value) {
        this.emitValue("");
        return;
      }

      // Verificar se está completo e válido
      if (this.isValidDisplayFormat(value)) {
        const momentDate = moment(value, this.expectedFormat);
        if (momentDate.isValid()) {
          this.lastValidValue = value;
          this.parseInternalValue();
          this.emitValue(value);
          return;
        }
      }

      // Se não for válido e não estiver vazio, reverter para último valor válido
      if (this.lastValidValue) {
        this.internalValue = this.lastValidValue;
      }
    },

    emitValue(value) {
      if (!this.isUpdatingFromProps) {
        this.$emit("input", value);
        this.$emit("change", value);
      }
    },

    openCalendar() {
      // Tentar definir data atual no picker
      if (this.internalValue && this.parseInternalValue()) {
        // Data já está definida
      } else if (!this.dateValue) {
        // Definir data de hoje por padrão
        this.dateValue = moment().format("YYYY-MM-DD");
      }
      this.menu = true;
    },

    onDateSelected() {
      if (!this.includeTime) {
        this.confirmDate();
      }
    },

    confirmDate() {
      if (this.dateValue) {
        const momentDate = moment(this.dateValue);
        this.internalValue = momentDate.format("DD/MM/YYYY");
        this.emitValue(this.internalValue);
      }
      this.menu = false;
    },

    confirmDateTime() {
      if (this.dateValue && this.timeValue) {
        const momentDate = moment(
          `${this.dateValue} ${this.timeValue}`,
          "YYYY-MM-DD HH:mm"
        );
        this.internalValue = momentDate.format("DD/MM/YYYY HH:mm");
        this.emitValue(this.internalValue);
      }
      this.showTimePicker = false;
      this.menu = false;
    },
  },
};
</script>

<style scoped>
/* Adicionar estilos se necessário */
</style>
